import { Fragment, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { CombatIdleConfig } from '../api/types';
import { iconePara } from '../assets/items';
import { ehArmaCombate } from '../data/armas';
import {
  ALVOS_IDLE,
  ANTIDOTO_PADRAO,
  ATAQUES_IDLE,
  BUFFS_IDLE,
  COMBATE_IDLE_CAP,
  COMBATE_IDLE_CAP_MANTER,
  COMBATE_IDLE_CAP_CURA,
  COMBATE_IDLE_VAZIO,
  COMBATE_IDLE_VERSAO,
  CURAS_IDLE,
  ITENS_CURA,
  type CombatIdleAlvo,
  type CombatIdleCura as CuraLinha
} from '../data/combateIdle';
import { ehConsumivel, ehGratis, ehPocaoVip } from '../data/consumiveis';
import { chanceDeSucesso, ehMagia, iconeDaMagia, MAGIAS, type MagiaId } from '../data/magias';
import { numero, t } from '../i18n/idioma';
import { nomeDaMagia, nomeDaSkill, nomeDoItem } from '../i18n/nomes';
import { useIconeTingido } from './useIconeTingido';
import { IcJogador, IcMana, IcMonstro, IcVida } from './icones/IconesV3';

const SAVE_MS = 400;
const LIMITE = { vida: [10, 90], mana: [0, 90] } as const;
/** Onde a cura nova entra: o primeiro % longe das outras. */
const PCTS_NOVA = [50, 70, 30, 80, 40, 60];

type Escolha = { tipo: 'ataque'; alvo: CombatIdleAlvo; pos: number } | { tipo: 'cura'; pos: number };

interface Props {
  /** Arma na mão principal, ou null. Entra no ciclo só se estiver num slot. */
  mainHandDefId?: string | null;
  /** VIP ativo: poção sem pilha mostra ∞. */
  vip?: boolean;
}

const iconeDe = (id: string) => (ehMagia(id) ? iconeDaMagia(id) : iconePara(id));
const nomeDe = (id: string) => (ehMagia(id) ? nomeDaMagia(id) : nomeDoItem(id));

/**
 * O % de uma cura ou da reserva de mana: − e + de 5 em 5, ou clicar e arrastar no meio.
 * Fica fora do CombateIdleConfig de propósito: definido lá dentro, ele virava um componente novo a cada
 * render e o React trocava os botões no meio do clique (o "salvando…" chega entre o apertar e o soltar
 * e o clique se perdia).
 */
function PilulaPct({ tipo, valor, ativo, onMudar }: {
  tipo: 'vida' | 'mana';
  valor: number;
  ativo: boolean;
  onMudar(v: number): void;
}) {
  const [lo, hi] = LIMITE[tipo];
  const arrasto = useRef<DOMRect | null>(null);
  const noPonto = (x: number) => {
    const r = arrasto.current;
    if (r) onMudar(((x - r.left) / r.width) * 100);
  };
  return (
    <span className={`cb-pct cb-pct--${tipo === 'vida' ? 'hp' : 'mn'}`} title={tipo === 'vida' ? t('cb.pctVida') : valor ? t('cb.pctMana') : t('cb.desligado')}>
      <span className="cb-pct__f" style={{ width: `${valor}%` }} />
      <button type="button" aria-label={t('cb.menos')} disabled={!ativo || valor <= lo} onClick={() => onMudar(valor - 5)}>−</button>
      <b
        onPointerDown={e => {
          if (!ativo) return;
          arrasto.current = e.currentTarget.parentElement!.getBoundingClientRect();
          e.currentTarget.setPointerCapture(e.pointerId);
          noPonto(e.clientX);
        }}
        onPointerMove={e => { if (arrasto.current) noPonto(e.clientX); }}
        onPointerUp={() => { arrasto.current = null; }}
        onPointerCancel={() => { arrasto.current = null; }}
      >
        {tipo === 'vida' ? <IcVida /> : <IcMana />}{valor ? `${valor}%` : t('cb.nunca')}
      </b>
      <button type="button" aria-label={t('cb.mais')} disabled={!ativo || valor >= hi} onClick={() => onMudar(valor + 5)}>+</button>
    </span>
  );
}

/**
 * Combate automático (techspec-hud-v3 §08): Atacar (Monstros e Players, três slots em ordem; a escolha
 * abre ali mesmo) e Curar (uma linha por cura com o % em − e +, até três; poção de mana; curar veneno em
 * interruptor). Grava sozinho. Players vazio usa o de Monstros (o servidor faz a mesma conta).
 */
export function CombateIdleConfig({ mainHandDefId, vip }: Props) {
  const [config, setConfig] = useState<CombatIdleConfig>(COMBATE_IDLE_VAZIO);
  const [ready, setReady] = useState(false);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [esc, setEsc] = useState<Escolha | null>(null);
  const [magery, setMagery] = useState<number | null>(null);
  const [estoque, setEstoque] = useState<Record<string, number>>({});
  const timer = useRef<number>(0);
  /** A config mais nova, para cliques seguidos somarem sobre ela e não sobre a do último render. */
  const atual = useRef<CombatIdleConfig>(COMBATE_IDLE_VAZIO);
  /** O que falta mandar ao servidor e se já tem um pedido no ar: um por vez, sempre o mais novo. */
  const pendente = useRef<CombatIdleConfig | null>(null);
  const noAr = useRef(false);
  const iconeArma = useIconeTingido(ehArmaCombate(mainHandDefId) ? mainHandDefId! : null);
  const icone = (id: string) => (ehArmaCombate(id) && id === mainHandDefId && iconeArma ? iconeArma : iconeDe(id));

  useEffect(() => {
    let vivo = true;
    api.getCombatIdle()
      .then(c => { if (vivo) { atual.current = c; setConfig(c); setReady(true); } })
      .catch(() => { if (vivo) { setErro(true); setReady(true); } });
    api.getProfile()
      .then(p => { if (vivo) setMagery(p.skills.find(s => s.id === 'magery')?.value ?? 0); })
      .catch(() => { if (vivo) setMagery(null); });
    api.getPack()
      .then(pack => {
        if (!vivo) return;
        const next: Record<string, number> = {};
        for (const i of pack.items) {
          if (i.equippedSlot || !ehConsumivel(i.defId)) continue;
          next[i.defId] = (next[i.defId] ?? 0) + i.qty;
        }
        setEstoque(next);
      })
      .catch(() => {});
    return () => {
      vivo = false;
      if (timer.current) window.clearTimeout(timer.current);
      // Fechou a janela antes da espera: a última mudança ainda vai.
      enviar();
    };
  }, []);

  function enviar() {
    timer.current = 0;
    if (noAr.current || !pendente.current) return;
    const v = pendente.current;
    pendente.current = null;
    noAr.current = true;
    api.setCombatIdle(v)
      .then(() => setErro(false))
      .catch(() => setErro(true))
      .finally(() => {
        noAr.current = false;
        if (pendente.current && !timer.current) enviar();
        else if (!pendente.current) setSalvando(false);
      });
  }

  const gravar = (next: CombatIdleConfig) => {
    const v = { ...next, versao: COMBATE_IDLE_VERSAO };
    atual.current = v;
    setConfig(v);
    setEsc(null);
    pendente.current = v;
    if (timer.current) window.clearTimeout(timer.current);
    setSalvando(true);
    timer.current = window.setTimeout(enviar, SAVE_MS);
  };

  const usarDe = (alvo: CombatIdleAlvo) => config.ataque.find(r => r.alvo === alvo)?.usar ?? [];
  const gravarAtaque = (alvo: CombatIdleAlvo, usar: readonly (string | null)[]) => {
    const ataque = ALVOS_IDLE.map(a => ({
      alvo: a,
      usar: a === alvo ? usar.filter((x): x is string => !!x).slice(0, COMBATE_IDLE_CAP) : [...usarDe(a)]
    }));
    gravar({ ...config, ataque });
  };

  /* como o protótipo: se já estava em outra posição, os dois trocam */
  const escolherAtaque = (alvo: CombatIdleAlvo, pos: number, id: string | null) => {
    const lista: (string | null)[] = [...usarDe(alvo)];
    while (lista.length < COMBATE_IDLE_CAP) lista.push(null);
    if (!id) lista[pos] = null;
    else {
      const k = lista.indexOf(id);
      if (k >= 0 && k !== pos) lista[k] = lista[pos];
      lista[pos] = id;
    }
    gravarAtaque(alvo, lista);
  };

  const curaDe = (id: string): CuraLinha => ({ usar: ehMagia(id) ? 'magia' : 'item', id, abaixoDe: 50 });
  const escolherCura = (pos: number, id: string | null) => {
    const curar = [...config.curar];
    if (!id) curar.splice(pos, 1);
    else if (pos < 0) {
      const livre = PCTS_NOVA.find(p => !curar.some(r => Math.abs(r.abaixoDe - p) < 10)) ?? 50;
      curar.push({ ...curaDe(id), abaixoDe: livre });
    } else curar[pos] = { ...curaDe(id), abaixoDe: curar[pos].abaixoDe };
    gravar({ ...config, curar: curar.slice(0, COMBATE_IDLE_CAP_CURA) });
  };
  const mudarPct = (tipo: 'vida' | 'mana', k: number, valor: number) => {
    const [lo, hi] = LIMITE[tipo];
    const v = Math.max(lo, Math.min(hi, Math.round(valor / 5) * 5));
    const c0 = atual.current;
    // Arrastar passa várias vezes pelo mesmo valor: só grava quando muda.
    if (tipo === 'vida') {
      if (c0.curar[k]?.abaixoDe === v) return;
      gravar({ ...c0, curar: c0.curar.map((c, i) => (i === k ? { ...c, abaixoDe: v } : c)) });
    } else {
      if (c0.reservaDeMana === v) return;
      gravar({ ...c0, reservaDeMana: v });
    }
  };

  /* Manter ativo (techspec-magias-de-efeito): liga e desliga o buff; a IA reconjura em si quando acaba. */
  const alternarManter = (id: string) => {
    const manter = config.manter ?? [];
    if (manter.includes(id)) gravar({ ...config, manter: manter.filter(x => x !== id) });
    else if (manter.length < COMBATE_IDLE_CAP_MANTER) gravar({ ...config, manter: [...manter, id] });
  };

  const arma = ehArmaCombate(mainHandDefId) ? mainHandDefId! : null;
  const ataques = [...(arma ? [arma] : []), ...ATAQUES_IDLE];
  const usadas = config.curar.map(c => c.id);
  const curas = [...ITENS_CURA, ...CURAS_IDLE] as string[];
  const chance = (id: string) => {
    const m = MAGIAS[id as MagiaId];
    return m && magery != null ? chanceDeSucesso(magery, m.skillMin, m.skillFull) : null;
  };
  const pct = (c: number | null) => (c == null ? '' : ` · ${Math.round(c * 100)}%`);
  const sub = (id: string) => {
    if (ehMagia(id)) return <small>{t('cb.magia')}{pct(chance(id))}</small>;
    if (ehGratis(id)) return <small>{t('combateIdle.cura.gratis')}</small>;
    const q = estoque[id] ?? 0;
    if (q > 0) return <small>×{numero(q)}</small>;
    return vip && ehPocaoVip(id) ? <small className="is-vip">∞ VIP</small> : <small>×0</small>;
  };


  // Arma na lista que não está na mão: o servidor pula o slot (HuntRegras.AcaoDaVez) e o ciclo vira só magia.
  // Antes isso era silencioso — o jogador via a adaga no 2º e o personagem só conjurava.
  const foraDaMao = (id: string) => ehArmaCombate(id) && id !== mainHandDefId;
  const armasFora = [...new Set(config.ataque.flatMap(r => r.usar).filter(foraDaMao))];

  const acimaDaSkill = config.ataque.flatMap(r => r.usar).some(id => {
    const m = MAGIAS[id as MagiaId];
    return !!m && magery !== null && magery < m.skillMin;
  });

  const tile = (id: string, onPick: () => void, marca = '') => {
    const c = chance(id);
    return (
      <button key={id} type="button" className="pk-t" title={`${nomeDe(id)}${pct(c)}`} onClick={onPick}>
        <img src={icone(id)} alt="" draggable={false} />
        {MAGIAS[id as MagiaId] && <span className="pk-t__m">{MAGIAS[id as MagiaId].mana}</span>}
        {marca && <span className="pk-t__k">{marca}</span>}
        <span className="pk-t__n">{nomeDe(id)}</span>
        {c != null && <span className="pk-t__c"><i style={{ width: `${c * 100}%`, background: c >= 0.7 ? '#5cbf3c' : c >= 0.35 ? '#e6c452' : '#d2453b' }} /></span>}
      </button>
    );
  };
  const vazio = (onPick: () => void, dica: string) => (
    <button type="button" className="pk-t pk-t--vazio" title={dica} aria-label={dica} onClick={onPick}>✕</button>
  );

  const curaAberta = esc?.tipo === 'cura' ? esc.pos : null;

  return (
    <>
      {erro && <p className="cb-nota is-erro" role="status">{t('combateIdle.naoGravou')}</p>}
      {acimaDaSkill && <p className="cb-nota" role="status">{t('combateIdle.acimaDaSkill', { skill: nomeDaSkill('magery') })}</p>}
      {armasFora.length > 0 && (
        <p className="cb-nota is-erro" role="status">{t('combateIdle.armaForaDaMao', { arma: armasFora.map(nomeDe).join(', ') })}</p>
      )}
      <section className="cb-sec">
        <h4 className="ch-sec">{t('cb.atacar')}<small>{t('cb.atacarDica')}</small></h4>
        {ALVOS_IDLE.map(alvo => {
          const usar = usarDe(alvo);
          const aberto = esc?.tipo === 'ataque' && esc.alvo === alvo ? esc.pos : null;
          return (
            <div key={alvo} className="cb-bloco">
              <div className="cb-alvo">
                <span className="cb-alvo__n">{alvo === 'monstro' ? <IcMonstro /> : <IcJogador />}{t(`cb.alvo.${alvo}`)}</span>
                <span className="cb-rot">
                  {[0, 1, 2].map(p => {
                    const id = usar[p];
                    return (
                      <Fragment key={p}>
                        {p > 0 && <i aria-hidden="true">›</i>}
                        <button
                          type="button"
                          className={`bb-s${id ? '' : ' is-vazio'}${id && foraDaMao(id) ? ' is-fora' : ''}${aberto === p ? ' is-on' : ''}`}
                          disabled={!ready}
                          aria-expanded={aberto === p}
                          title={id ? `${p + 1}º · ${nomeDe(id)}${foraDaMao(id) ? ` · ${t('combateIdle.foraDaMao')}` : ''}` : `${p + 1}º · ${t('combateIdle.vazio')}`}
                          aria-label={id ? `${p + 1}º · ${nomeDe(id)}` : `${p + 1}º · ${t('combateIdle.vazio')}`}
                          onClick={() => setEsc(aberto === p ? null : { tipo: 'ataque', alvo, pos: p })}
                        >
                          {id && <img src={icone(id)} alt="" draggable={false} />}
                          <kbd>{p + 1}</kbd>
                        </button>
                      </Fragment>
                    );
                  })}
                </span>
              </div>
              {aberto !== null && (
                <div className="cb-esc">
                  {ataques.map(id => {
                    const k = usar.indexOf(id);
                    return tile(id, () => escolherAtaque(alvo, aberto, id), k >= 0 && k !== aberto ? `${k + 1}º` : '');
                  })}
                  {usar[aberto] && vazio(() => escolherAtaque(alvo, aberto, null), t('cb.deixarVazio'))}
                </div>
              )}
            </div>
          );
        })}
        {usarDe('jogador').length === 0 && <p className="cb-nota">{t('cb.playersVazio')}</p>}
      </section>

      <section className="cb-sec">
        <h4 className="ch-sec">{t('cb.curar')}<small>{t('cb.curarDica')}</small></h4>
        {config.curar.map((r, k) => (
          <div key={`${r.usar}:${r.id}`} className="cb-bloco">
            <div className="cb-ln">
              <button
                type="button"
                className="cb-ln__ic"
                disabled={!ready}
                aria-expanded={curaAberta === k}
                title={t('cb.trocar')}
                aria-label={`${t('cb.trocar')} · ${nomeDe(r.id)}`}
                onClick={() => setEsc(curaAberta === k ? null : { tipo: 'cura', pos: k })}
              >
                <img src={iconeDe(r.id)} alt="" />
              </button>
              <span className="cb-ln__n">{nomeDe(r.id)}{sub(r.id)}</span>
              <PilulaPct tipo="vida" valor={r.abaixoDe} ativo={ready} onMudar={v => mudarPct('vida', k, v)} />
              <button type="button" className="cb-x" disabled={!ready} aria-label={t('cb.tirar')} title={t('cb.tirar')} onClick={() => escolherCura(k, null)}>✕</button>
            </div>
            {curaAberta === k && (
              <div className="cb-esc">
                {curas.filter(id => id === r.id || !usadas.includes(id)).map(id => tile(id, () => escolherCura(k, id)))}
              </div>
            )}
          </div>
        ))}
        {curaAberta === -1 && (
          <div className="cb-esc">
            {curas.filter(id => !usadas.includes(id)).map(id => tile(id, () => escolherCura(-1, id)))}
          </div>
        )}
        {config.curar.length < COMBATE_IDLE_CAP_CURA && (curaAberta === -1 ? (
          <button type="button" className="cb-add cb-add--cancelar" onClick={() => setEsc(null)}>{t('comum.cancelar')}</button>
        ) : (
          <button type="button" className="cb-add" disabled={!ready} onClick={() => setEsc({ tipo: 'cura', pos: -1 })}>{t('cb.maisCura')}</button>
        ))}
        <div className="cb-ln">
          <span className="cb-ln__ic" aria-hidden="true"><img src={iconeDe('mana')} alt="" /></span>
          <span className="cb-ln__n">{nomeDe('mana')}{sub('mana')}</span>
          <PilulaPct tipo="mana" valor={config.reservaDeMana} ativo={ready} onMudar={v => mudarPct('mana', 0, v)} />
          <span />
        </div>
        <div className="cb-ln">
          <span className="cb-ln__ic" aria-hidden="true"><img src={iconeDaMagia(ANTIDOTO_PADRAO)} alt="" /></span>
          <span className="cb-ln__n">
            {t('cb.veneno')}
            <small>{config.veneno ? `${t('cb.magia')}${pct(chance(ANTIDOTO_PADRAO))}` : t('cb.desligado')}</small>
          </span>
          <button
            type="button"
            className="cb-sw"
            disabled={!ready}
            aria-pressed={!!config.veneno}
            aria-label={t('cb.veneno')}
            onClick={() => gravar({ ...config, veneno: config.veneno ? null : { usar: 'magia', id: ANTIDOTO_PADRAO } })}
          />
          <span />
        </div>
      </section>
      <section className="cb-sec">
        <h4 className="ch-sec">{t('cb.manter')}<small>{t('cb.manterDica', { n: COMBATE_IDLE_CAP_MANTER })}</small></h4>
        <div className="cb-esc cb-manter">
          {BUFFS_IDLE.map(id => {
            const on = (config.manter ?? []).includes(id);
            const cheio = !on && (config.manter ?? []).length >= COMBATE_IDLE_CAP_MANTER;
            const c = chance(id);
            return (
              <button key={id} type="button" className={`pk-t${on ? ' is-on' : ''}`} aria-pressed={on}
                disabled={!ready || cheio} title={`${nomeDe(id)}${pct(c)}`} onClick={() => alternarManter(id)}>
                <img src={iconeDaMagia(id as MagiaId)} alt="" draggable={false} />
                <span className="pk-t__m">{MAGIAS[id as MagiaId].mana}</span>
                {on && <span className="pk-t__k">✓</span>}
                <span className="pk-t__n">{nomeDe(id)}</span>
                {c != null && <span className="pk-t__c"><i style={{ width: `${c * 100}%`, background: c >= 0.7 ? '#5cbf3c' : c >= 0.35 ? '#e6c452' : '#d2453b' }} /></span>}
              </button>
            );
          })}
        </div>
      </section>
      <p className={`cb-pe${!salvando && !erro ? ' is-ok' : ''}`} role="status">
        {salvando ? t('cb.salvando') : erro ? '' : t('cb.salvo')}
      </p>
    </>
  );
}
